import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUsers, FaSearch, FaFilter, FaGraduationCap, FaEnvelope, FaPhone, FaUniversity, FaBook, FaProjectDiagram, FaCalendarAlt, FaCreditCard } from 'react-icons/fa';
import Toast from '../components/Toast';
import * as authService from '../services/authService';
import * as adminService from '../services/adminService';

const YoungIndiaStudentManagement = () => {
    const [students, setStudents] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const navigate = useNavigate();

    // Check if user is admin
    useEffect(() => {
        const user = authService.getCurrentUser();
        if (!user) {
            navigate('/login');
            return;
        }
        if (user.role !== 'admin') {
            navigate('/');
            return;
        }

        loadStudents();
    }, [navigate]);

    const loadStudents = async () => {
        setLoading(true);
        try {
            const response = await adminService.getYoungIndiaStudents();
            if (response && response.success) {
                setStudents(response.data || []);
            } else {
                setStudents([]);
                showToast(response?.message || 'Failed to load students', 'error');
            }
        } catch (error) {
            console.error('Error loading Young India students:', error);
            showToast('Failed to load student data from server', 'error');
            setStudents([]);
        } finally {
            setLoading(false);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    const getFilteredStudents = () => {
        let filtered = [...students];

        if (searchTerm && searchTerm.trim() !== '') {
            const search = searchTerm.toLowerCase().trim();
            filtered = filtered.filter(student => {
                const name = (student.full_name || '').toLowerCase();
                const email = (student.email || '').toLowerCase();
                const phone = (student.phone_number || '');
                const college = (student.student_profile?.university || student.student_profile?.college_name || '').toLowerCase();

                return name.includes(search) ||
                    email.includes(search) ||
                    phone.includes(search) ||
                    college.includes(search);
            });
        }

        return filtered;
    };

    const filteredStudents = getFilteredStudents();

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-primary-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading Young India Students...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            <div className="max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="bg-white rounded-3xl shadow-xl p-8 mb-8 border border-white/50 backdrop-blur-sm">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="flex items-center gap-5">
                            <div className="w-16 h-16 bg-gradient-to-br from-primary-blue to-secondary-blue rounded-2xl flex items-center justify-center shadow-lg transform rotate-3">
                                <FaGraduationCap className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Young India Management</h1>
                                <p className="text-gray-500 mt-1 font-medium italic">Track and manage registered young researchers</p>
                            </div>
                        </div>
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 px-6 py-4 rounded-2xl border border-blue-100 flex flex-col items-end">
                            <p className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-1">Total Registered</p>
                            <p className="text-4xl font-black text-primary-blue leading-none">{students.length}</p>
                        </div>
                    </div>
                </div>

                {/* Search & Utilities */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 mb-8 border border-white/50">
                    <div className="relative">
                        <FaSearch className="absolute left-5 top-1/2 transform -translate-y-1/2 text-primary-blue/50 text-lg" />
                        <input
                            type="text"
                            placeholder="Search by student name, email, phone or college..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-14 pr-6 py-4 bg-gray-50/50 border-2 border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-blue/10 focus:border-primary-blue transition-all duration-300 text-lg font-medium"
                        />
                    </div>
                </div>

                {/* Students List */}
                {filteredStudents.length === 0 ? (
                    <div className="bg-white rounded-3xl shadow-xl p-20 text-center border border-white/50">
                        <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <FaUsers className="w-12 h-12 text-gray-300" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">No Students Found</h3>
                        <p className="text-gray-500 max-w-md mx-auto">
                            {searchTerm 
                                ? "We couldn't find any Young India students matching your search criteria." 
                                : "There are currently no Young India students registered in the system."}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-6">
                        {filteredStudents.map((student, index) => (
                            <div 
                                key={student.id || index}
                                className="group bg-white rounded-3xl shadow-md hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 hover:border-primary-blue/20"
                            >
                                <div className="p-8">
                                    <div className="flex flex-col lg:flex-row gap-8">
                                        {/* Avatar and Primary Info */}
                                        <div className="flex-shrink-0 flex flex-col items-center">
                                            <div className="w-24 h-24 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl flex items-center justify-center shadow-inner relative overflow-hidden group-hover:from-blue-50 group-hover:to-indigo-50 transition-colors duration-500">
                                                <span className="text-4xl font-black text-primary-blue/30 group-hover:text-primary-blue/50 transition-colors duration-500">
                                                    {(student.full_name || 'U').charAt(0).toUpperCase()}
                                                </span>
                                                {student.payment_status === 'PAID' && (
                                                    <div className="absolute top-0 right-0 w-8 h-8 bg-green-500 flex items-center justify-center rounded-bl-xl shadow-lg">
                                                        <FaCreditCard className="text-white text-xs" title="Payment Verified" />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="mt-4 flex flex-col items-center">
                                                <span className={`px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest ${
                                                    student.payment_status === 'PAID' 
                                                        ? 'bg-green-100 text-green-700' 
                                                        : 'bg-amber-100 text-amber-700'
                                                }`}>
                                                    {student.payment_status || 'PENDING'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Main Content */}
                                        <div className="flex-grow grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                            {/* Contact Details */}
                                            <div className="space-y-4">
                                                <div>
                                                    <h3 className="text-2xl font-black text-gray-900 leading-tight group-hover:text-primary-blue transition-colors duration-300">
                                                        {student.full_name || 'Anonymous User'}
                                                    </h3>
                                                    <div className="mt-3 space-y-2">
                                                        <p className="flex items-center gap-3 text-gray-600 font-semibold text-sm">
                                                            <FaEnvelope className="text-primary-blue/60" />
                                                            {student.email}
                                                        </p>
                                                        <p className="flex items-center gap-3 text-gray-600 font-semibold text-sm">
                                                            <FaPhone className="text-primary-blue/60" />
                                                            {student.phone_number || 'No contact number'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Academic Info */}
                                            <div className="space-y-4">
                                                <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest">Academic Details</h4>
                                                <div className="space-y-3">
                                                    <p className="flex items-start gap-3 text-gray-700 font-bold text-sm">
                                                        <FaUniversity className="mt-1 text-primary-blue/60 flex-shrink-0" />
                                                        <span>{student.student_profile?.university || student.student_profile?.college_name || 'N/A'}</span>
                                                    </p>
                                                    <p className="flex items-center gap-3 text-gray-700 font-bold text-sm">
                                                        <FaBook className="text-primary-blue/60 flex-shrink-0" />
                                                        <span>{student.student_profile?.subject?.name || student.student_profile?.department || 'N/A'}</span>
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Stream/Experience */}
                                            <div className="space-y-4">
                                                <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest">Stream & Specialization</h4>
                                                <div className="space-y-3">
                                                    <p className="flex items-center gap-3 text-gray-700 font-bold text-sm">
                                                        <FaProjectDiagram className="text-primary-blue/60 flex-shrink-0" />
                                                        <span>{student.student_profile?.stream?.name || student.student_profile?.stream_name || 'N/A'}</span>
                                                    </p>
                                                    <div className="flex items-center gap-2 mt-4 px-4 py-2 bg-gray-50 rounded-xl w-fit">
                                                        <FaCalendarAlt className="text-gray-400 text-xs" />
                                                        <span className="text-xs font-bold text-gray-500">
                                                            Registered: {new Date(student.created_at).toLocaleDateString('en-IN', { 
                                                                day: '2-digit', 
                                                                month: 'short', 
                                                                year: 'numeric' 
                                                            })}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default YoungIndiaStudentManagement;
